import type { ErroDeLinha, TipoErroLinha } from './validacao'

// Como cada problema se chama para quem esta lendo. Frase curta e no singular:
// a quantidade vem ao lado ("Preço inválido — 137 linhas"), entao pluralizar
// aqui sairia errado quando for uma linha so.
const ROTULO: Record<TipoErroLinha, string> = {
  codigo_vazio: 'Código vazio',
  descricao_vazia: 'Descrição vazia',
  preco_invalido: 'Preço inválido',
  comissao_invalida: 'Comissão inválida',
  quantidade_minima_invalida: 'Quantidade mínima inválida',
  quantidade_maxima_invalida: 'Quantidade máxima inválida',
  quantidade_maxima_menor_que_minima: 'Quantidade máxima menor que a mínima',
  fator_invalido: 'Unidades por embalagem inválida',
  preco_unidade_invalido: 'Preço avulso inválido',
  ipi_invalido: 'IPI inválido',
  icms_invalido: 'ICMS inválido',
  st_invalido: 'ST inválido',
  pis_cofins_invalido: 'PIS/COFINS inválido',
  razao_social_vazia: 'Razão social vazia',
  // Alíquotas fiscais (validarAliquotas.ts, Task 8).
  tipo_de_linha_desconhecido: 'Linha não reconhecida (ICMS, ST ou desconto de frete)',
  codigo_produto_vazio: 'Código do produto vazio',
  uf_destino_vazia: 'UF de destino vazia',
  uf_destino_invalida: 'UF de destino inválida',
  aliquota_pct_ausente: 'Alíquota ICMS % ausente',
  aliquota_pct_invalida: 'Alíquota ICMS % inválida',
  mva_pct_ausente: 'MVA % ausente',
  mva_pct_invalido: 'MVA % inválido',
  regiao_destino_vazia: 'Região de destino vazia',
  incoterm_vazio: 'Incoterm vazio',
  incoterm_invalido: 'Incoterm inválido',
  desconto_pct_ausente: 'Desconto de frete % ausente',
  desconto_pct_invalido: 'Desconto de frete % inválido',
}

export interface GrupoDeErro {
  tipo: TipoErroLinha
  rotulo: string
  quantidade: number
  // As primeiras ocorrencias, para ela saber ONDE olhar e O QUE consertar.
  exemplos: Array<{ linha: number; valor?: string }>
  // Quantas ficaram de fora dos exemplos. Zero esconde o "e mais N".
  restantes: number
}

// Junta as linhas que falharam PELO MESMO MOTIVO.
//
// Existe porque a previa listava uma mensagem por linha e cortava em 50 sem
// dizer que cortou: com 200 linhas de preco invalido, ela lia 50 frases quase
// iguais e nao ficava sabendo das outras 150. O que ela precisa saber e "o que
// ha de errado com a minha planilha", e isso e uma linha por problema.
//
// Agrupa por TIPO, nao pelo texto do motivo: o motivo embute o valor ofensivo,
// entao 137 precos invalidos com valores diferentes virariam 137 grupos de 1.
export function agruparErros(erros: readonly ErroDeLinha[], maxExemplos = 3): GrupoDeErro[] {
  const porTipo = new Map<TipoErroLinha, ErroDeLinha[]>()
  for (const erro of erros) {
    const lista = porTipo.get(erro.tipo) ?? []
    lista.push(erro)
    porTipo.set(erro.tipo, lista)
  }

  const grupos: GrupoDeErro[] = []
  for (const [tipo, lista] of porTipo) {
    const exemplos = lista
      .slice(0, Math.max(0, maxExemplos))
      .map((e) => (e.valor === undefined ? { linha: e.linha } : { linha: e.linha, valor: e.valor }))
    grupos.push({
      tipo,
      rotulo: ROTULO[tipo],
      quantidade: lista.length,
      exemplos,
      restantes: lista.length - exemplos.length,
    })
  }

  // O problema que mais atrapalha primeiro. Empate desempata pela primeira
  // linha, para a ordem nao dancar entre duas leituras da mesma planilha.
  grupos.sort((a, b) => {
    if (b.quantidade !== a.quantidade) return b.quantidade - a.quantidade
    return (a.exemplos[0]?.linha ?? 0) - (b.exemplos[0]?.linha ?? 0)
  })
  return grupos
}
